import { EXTENSIONS_NOTES } from '@constants/extension-codes';

import type {
  Narrative,
  Extension,
  Meta,
  Annotation,
  ResourceAssociationUnion,
  CurationMeta,
  CurationStatus,
  Attachment,
} from 'generated/types';
import { ResourceSourceType } from 'generated/types';

interface FhirResourceModel {
  id?: string | null;
  meta?: Meta | null;
  implicitRules?: string | null;
  language?: string | null;
  text?: Narrative | null;
  extension?: Extension[] | null;
  resourceType?: string | null;
  associatedResources?: ResourceAssociationUnion[] | null;
  attachments?: Attachment[] | null;
  curationMeta?: CurationMeta | null;
  curationStatus?: CurationStatus | null;
  recordSource?: ResourceSourceType | null;
}

export type BaseFhirFormValues = Pick<FhirResource, 'recordSource'> & {
  toPublish?: boolean | null;
  submit: string | null;
  files: DroppedFile[];
};

export type GetFhirRecordParams = {
  record_id: string;
  skip?: boolean;
};

export class FhirResource {
  public static readonly VITALL_SYSTEM = 'https://vitall.com/documentation';

  private static readonly EXTENSION_URL =
    'https://vitall.com/documentation/extensions/';

  private static SHAREABLE_RESOURCES = [
    'DiagnosticReport',
    'Immunization',
    'Procedure',
    'Observation',
    'Appointment',
    'AllergyIntolerance',
    'Encounter',
    'Condition',
    'MedicationStatement',
    'FamilyMemberHistory',
    'Coverage',
  ];

  protected model: FhirResourceModel;

  public constructor(model: FhirResourceModel) {
    this.model = model;
  }

  /**
   * Record identifier
   */
  public get id() {
    return this.model.id;
  }

  /**
   * Resource type
   */
  public get resourceType() {
    return this.model.resourceType;
  }

  /**
   * Record notes
   */
  public get recordNotes(): Annotation[] {
    const extension = this.findExtensions(EXTENSIONS_NOTES);
    return (extension ?? [])
      .map(({ valueAnnotation }) => valueAnnotation)
      .filter((a): a is Annotation => Boolean(a));
  }

  /**
   * Curation metadata
   */
  public get lastUpdate() {
    return this.model.meta?.lastUpdated;
  }

  /**
   * Curation metadata
   */
  public get curationMeta() {
    return this.model.curationMeta;
  }

  public set curationMeta(meta: CurationMeta | null | undefined) {
    this.model = { ...this.model, curationMeta: meta };
  }

  /**
   * Curation status
   */
  public get curationStatus() {
    return this.model.curationMeta?.status as CurationStatus | undefined;
  }

  public set curationStatus(status: CurationStatus | undefined) {
    this.model = { ...this.model, curationStatus: status };
  }

  /**
   * Record source type
   */
  public get recordSource() {
    return this.model.curationMeta?.source_type as
      ResourceSourceType | undefined;
  }

  /**
   * Whether the client has viewed the record or not
   */
  public get viewedStatus() {
    return this.model.curationMeta?.viewed ?? false;
  }

  /**
   * Whether the record is reported by the client or not
   */
  public get selfReported() {
    return this.recordSource === ResourceSourceType.self;
  }

  /**
   * Source of the record
  public get recordSource() {
    const extension = this.findExtension(EXTENSIONS_RECORD_SOURCE);
    return extension?.valueString;
  }
  */

  /**
   * Records associated with the current record
   */
  public get associatedRecords() {
    return this.model.associatedResources ?? [];
  }

  /**
   * Records associated with the current record
   */
  public set associatedRecords(associations: ResourceAssociationUnion[]) {
    this.model = { ...this.model, associatedResources: associations };
  }

  /**
   * Record attachments
   */
  public get attachments(): Attachment[] {
    return this.model.attachments ?? [];
  }

  public static stripNulls(obj: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    Object.entries(obj).forEach(([k, v]) => {
      if (v && typeof v === 'object') {
        FhirResource.stripNulls(v);
      }
      if (
        (v &&
          typeof v === 'object' &&
          !Array.isArray(v) &&
          !Object.keys(v).length) ||
        v === null ||
        v === undefined
      ) {
        if (Array.isArray(obj)) {
          obj.splice(k as unknown as number, 1);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-dynamic-delete
          delete obj[k];
        }
      }
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return obj;
  }

  /**
   * Pads an extension url with EXTENSION_URL if is not already
   * @param url Extension Url or path
   */
  protected static padExtensionUrl(url: string): string {
    if (url.startsWith(FhirResource.EXTENSION_URL)) {
      return url;
    }
    return `${FhirResource.EXTENSION_URL}${url}`;
  }

  /**
   * Get a cleaned version of a FHIR resource model
   * @param model FHIR resource model
   * @returns Cleaned model, used for inputs
   */
  protected static getCleanInput<T extends FhirResourceModel>(model: T) {
    const cleaned = FhirResource.clean(model);
    delete cleaned.id;
    delete cleaned.meta;
    delete cleaned.associatedResources;
    delete cleaned.attachments;
    delete cleaned.curationMeta;
    return cleaned;
  }

  /**
   * Strip null and __typename from a FHIR resource
   * @param record FHIR Resource
   * @returns The orginal FHIR resource, with __typename and null values removed
   */
  private static clean<T>(record: T) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return FhirResource.stripNulls(FhirResource.stripTypenames(record)) as T;
  }

  private static stripTypenames(record: any): any {
    if (Array.isArray(record)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return record.map(FhirResource.stripTypenames);
    } else if (record !== null && typeof record === 'object') {
      const newObject: Record<string, unknown> = {};
      for (const property in record) {
        if (property !== '__typename') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          newObject[property] = FhirResource.stripTypenames(record[property]);
        }
      }
      return newObject;
    }
    return record;
  }

  /**
   * Record identifier, prefixed with resource type
   */
  public getResourceId() {
    return `${this.model.resourceType}/${this.model.id}`;
  }

  /**
   * Record identifier, not prefixed with resource type
   */
  public getId() {
    return `${this.model.id}`;
  }

  public isShareable() {
    return (
      this.resourceType &&
      FhirResource.SHAREABLE_RESOURCES.includes(this.resourceType)
    );
  }

  /**
   * Find an extension of a specific type
   * @param code Extension code
   * @returns Extension matching the given code
   */
  protected findExtension(code: string) {
    const extension = this.model.extension?.find((e) => e.url.endsWith(code));
    return extension;
  }

  /**
   * Find the index of an extension of a specific type
   * @param code Extension code
   * @returns Index of the extension matching the given code
   */
  protected findExtensionIndex(code: string) {
    const index = this.model.extension?.findIndex((e) => e.url.endsWith(code));
    return index ?? -1;
  }

  /**
   * Find all extensions of a specific type
   * @param code Extension code
   * @returns All extensions matching the given code
   */
  protected findExtensions(code: string) {
    const extension = this.model.extension?.filter((e) => e.url.endsWith(code));
    return extension;
  }

  protected addOrReplaceExtension(extension: Extension) {
    extension.url = FhirResource.padExtensionUrl(extension.url);
    if (!this.model.extension) {
      this.model = { ...this.model, extension: [] };
    }
    // Normalize URLs to collapse double slashes (e.g. extensions//dose -> extensions/dose)
    // so that duplicates from the extra-slash bug are deduplicated
    const normalizeUrl = (url: string) => url.replace(/([^:])\/\//g, '$1/');
    const normalizedUrl = normalizeUrl(extension.url);
    extension.url = normalizedUrl;
    const extensions = [...(this.model.extension ?? [])].filter(
      ({ url }) => normalizeUrl(url) !== normalizedUrl
    );
    extensions.push(extension);
    this.model = { ...this.model, extension: extensions };
  }

  protected addOrReplaceExtensionCoding(
    url: string,
    system: string,
    value: string | null | undefined
  ) {
    // Find index of extension
    let extensionIndex = this.findExtensionIndex(url);
    let extensions = [...(this.model.extension ?? [])];
    if (extensionIndex === -1) {
      extensions.push({
        url: FhirResource.padExtensionUrl(url),
        valueCodeableConcept: {
          coding: [],
          text: value,
        },
      });
      extensionIndex = extensions.length - 1;
    }
    // Populate extension coding with new value
    const extension = extensions[extensionIndex];
    let coding = [...(extension.valueCodeableConcept?.coding ?? [])];
    const index = coding.findIndex(({ system: _system }) => system === _system);
    if (value) {
      if (index > -1) {
        coding[index] = {
          display: value,
          system,
        };
      } else {
        coding.push({
          display: value,
          system,
        });
      }
      extensions[extensionIndex] = {
        url: FhirResource.padExtensionUrl(url),
        valueCodeableConcept: {
          coding,
          text: value,
        },
      };
    } else {
      // If no value, rm related coding
      coding = coding.filter(({ system: _system }) => system !== _system);
      if (!coding.length) {
        // If no more coding, rm extension
        extensions = extensions.filter(
          ({ url, valueCodeableConcept }) =>
            url === FhirResource.padExtensionUrl(url) &&
            Boolean(valueCodeableConcept)
        );
      }
    }
    this.model = { ...this.model, extension: extensions };
  }

  /**
   * Filter out all extensions that match the given url
   * @param extension_url URL of the extension(s) to remove
   */
  protected removeExtension(extension_url: string) {
    extension_url = FhirResource.padExtensionUrl(extension_url);
    const extensions = [...(this.model.extension ?? [])].filter(
      ({ url }) => url !== extension_url
    );
    this.model = { ...this.model, extension: extensions };
  }
}
